// SPDX-License-Identifier: MIT
// Modern Redux store for Atlas Viewer production app
// NOTE: Keep typings explicit; avoid broad ts-ignore / ts-nocheck.

import {
  combineReducers,
  createStore,
  applyMiddleware,
  compose,
  Middleware,
  StoreEnhancer,
} from 'redux';
import { createLogger } from 'redux-logger';
import { enhanceReduxMiddleware } from '@kepler.gl/reducers';
import { exportControlMiddleware } from './middleware/export-control-middleware';
import keplerGlReducer from '@kepler.gl/reducers';
import {
  DEFAULT_MAP_LATITUDE,
  DEFAULT_MAP_LONGITUDE,
  DEFAULT_MAP_ZOOM,
  ATLAS_DARK_STYLE_ID,
  FALLBACK_STYLE_ID,
} from './constants/default-settings';
import { getDefaultStyleId, getMapboxToken } from './utils/mapbox-config';
import { azureReducer } from './cloud-providers/azure/azure-slice';
import { sqlPanelReducer } from './integrations/sql-panel/sql-panel-slice';
import { featureAnnotationsReducer } from './integrations/feature-annotations/feature-annotations-slice';

// Custom map style configuration from environment
const MAPBOX_TOKEN = getMapboxToken() || '';
const MAPBOX_STYLE_URL = import.meta.env.VITE_MAPBOX_STYLE_URL || '';

// Determine the default style ID based on available configuration
const defaultStyleId = getDefaultStyleId();

console.log('Atlas Viewer Store Configuration:', {
  hasMapboxToken: Boolean(MAPBOX_TOKEN),
  hasStyleUrl: Boolean(MAPBOX_STYLE_URL),
  defaultStyleId,
  atlasStyleId: ATLAS_DARK_STYLE_ID,
  fallbackStyleId: FALLBACK_STYLE_ID,
});

// Customized Kepler.gl reducer with initial map state
const customizedKeplerGlReducer = keplerGlReducer.initialState({
  mapStyle: {
    styleType: defaultStyleId,
    // Let Kepler.gl use its built-in default styles naturally
    // Custom styles are added via App.tsx mapStyles prop if Mapbox config is valid
  },
  mapState: {
    bearing: 0,
    dragRotate: false,
    latitude: DEFAULT_MAP_LATITUDE,
    longitude: DEFAULT_MAP_LONGITUDE,
    pitch: 0,
    zoom: DEFAULT_MAP_ZOOM,
    isSplit: false,
  },
  uiState: {
    geocoder: {
      enabled: false,
    },
    // Add more UI state customizations as needed
  },
});

// Combine reducers
const reducers = combineReducers({
  keplerGl: customizedKeplerGlReducer,
  azure: azureReducer,
  sqlPanel: sqlPanelReducer,
  featureAnnotations: featureAnnotationsReducer,
});

// Configure middlewares for Atlas Viewer (typed)
// Use loose typing for kepler.gl enhanced middleware (lib doesn't expose stricter generics)
const baseMiddlewares: Middleware[] = [exportControlMiddleware];
export const middlewares = enhanceReduxMiddleware(baseMiddlewares as any) as Middleware[];

// Add logger only in development (avoid including in production bundle)
if (import.meta.env.DEV) {
  const logger = createLogger({ collapsed: () => true });
  middlewares.push(logger as unknown as Middleware);
}

export const enhancers: StoreEnhancer[] = [applyMiddleware(...middlewares)];

const initialState = {};

// Redux DevTools setup for development
let composeEnhancers = compose;

// Support Redux DevTools if present
declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: (options?: Record<string, unknown>) => typeof compose;
  }
}

if (
  typeof window !== 'undefined' &&
  typeof window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ === 'function'
) {
  composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
    actionsBlacklist: [
      '@@kepler.gl/MOUSE_MOVE',
      '@@kepler.gl/UPDATE_MAP',
      '@@kepler.gl/LAYER_HOVER',
    ],
  }) as typeof compose;
}

const store = createStore(
  reducers,
  initialState,
  composeEnhancers(...(enhancers as [StoreEnhancer]))
);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
